const set = require('lodash/set')
const get = require('lodash/get')
const keyBy = require('lodash/keyBy')
const getSnapshotId = require('./tools/get-snapshot-id')
const toDocIdProperties = require('./tools/to-doc-id-properties')
const toLocationProperties = require('./tools/to-location-properties')
const { getServiceForLocationId } = require('../service/tools/territory-lookup')

/**
 * @typedef {Object} MainApi
 * @property {import('./index').ShipmentApi} shipment
 * @property {import('../product')} product
 */

/**
 * @typedef {'CouchProduct'} PriceSource
*/

class ShipmentPriceListApi {
  /**
   * @param {MainApi} mainApi
   */
  constructor (mainApi, pgConnection) {
    this.mainApi = mainApi
    this.pgConnection = pgConnection
  }

  /**
   * Resolve shipment prices for a set of shipment descriptors.
   * Price priority is:
   * 1. origin location prices
   * 2. service prices
   * 3. general prices
   *
   * @param {Array<{
   *   originId: string,
   *   destinationId?: string,
   *   referenceDate?: string,
   *   productIds: string[],
   *   serviceId?: string,
   *   planningType?: string
   * }>} shipments
   * @returns {Promise<Array<{
   *   originId: string,
   *   destinationId?: string,
   *   referenceDate?: string,
   *   productIds: string[],
   *   serviceId?: string,
   *   prices: Array<any>,
   *   pricesByProductId: Object<string, any>
   * }>>}
   */
  async resolveShipmentPrices (shipments = [], pgClient) {
    if (!Array.isArray(shipments)) {
      throw new Error('Shipments array is required')
    }

    const { client, shouldRelease } = pgClient ? { client: pgClient, shouldRelease: false } : await this.getPgClient()

    try {
      return Promise.all(shipments.map(async shipment => {
        const {
          originId,
          destinationId,
          referenceDate,
          productIds = [],
          serviceId,
          planningType
        } = shipment || {}

        if (!originId) {
          throw new Error('Shipment originId is required')
        }

        if (!Array.isArray(productIds)) {
          throw new Error('Shipment productIds must be an array')
        }

        const uniqueProductIds = [...new Set(productIds.filter(Boolean))]
        const destinationServiceId = destinationId
          ? get(getServiceForLocationId(destinationId), 'service', serviceId)
          : serviceId
        const translatedProductIdsByOriginalId = await this.getTranslatedProductIdsForService(
          uniqueProductIds,
          destinationServiceId,
          client
        )
        const translatedProductIds = uniqueProductIds.map(productId => translatedProductIdsByOriginalId[productId] || productId)
        const translatedPricesByProductId = await this.getResolvedPricesForShipment({
          locationId: originId,
          serviceId: destinationServiceId,
          referenceDate,
          productIds: translatedProductIds,
          pgClient: client,
          planningType
        })
        const pricesByProductId = uniqueProductIds.reduce((acc, productId) => {
          const translatedProductId = translatedProductIdsByOriginalId[productId] || productId
          const translatedPrice = translatedPricesByProductId[translatedProductId] || {}
          acc[productId] = {
            ...translatedPrice,
            sku: productId
          }
          return acc
        }, {})

        return {
          originId,
          destinationId,
          referenceDate,
          productIds: uniqueProductIds,
          serviceId,
          prices: Object.values(pricesByProductId),
          pricesByProductId,
          translatedProductIdsByOriginalId
        }
      }))
    } finally {
      if (shouldRelease) {
        client.release()
      }
    }
  }

  /**
   * @param {string} shipmentId
   * @param {Object.<string, {
   *   quantity: number
   *   paymentType: string
   *   checked: boolean
   *   skuMarket: string
   *   destinationSku: string
   *   originSku: string
   * }>} countsWithTranslations
   * @param {PriceSource} priceSource
   */
  async upsert (shipmentId, countsWithTranslations, priceSource) {
    if (!countsWithTranslations) {
      throw new Error('Counts with sku translations required')
    }

    const shipment = await this.mainApi.shipment.findById(shipmentId)
    const referenceDate = new Date(shipment.date)
    const timeStamp = new Date().toJSON()
    const unitPrices = await this.getUnitPrices(shipment, countsWithTranslations, priceSource, referenceDate)

    // update price list snapshot
    if (shipment.priceListId) {
      const priceSnapshot = await this.mainApi.state.shipmentsDb.get(shipment.priceListId)
      const updatedUnitPrices = { ...get(priceSnapshot, 'unitPrices', {}), ...unitPrices }
      set(priceSnapshot, 'unitPrices', updatedUnitPrices)
      set(priceSnapshot, 'updatedAt', timeStamp)
      set(priceSnapshot, 'priceReferenceDate', referenceDate.toJSON())
      return this.mainApi.state.shipmentsDb.bulkDocs([priceSnapshot])
    }

    const idProps = toDocIdProperties(shipment.id)
    const id = getSnapshotId(
      { device: { id: 0 } },
      {
        status: idProps.status,
        shipmentNo: idProps.shipmentNo,
        date: idProps.date,
        origin: toLocationProperties(idProps.origin),
        destination: toLocationProperties(idProps.destination)
      }
    ) + `:price:list`

    const snapshot = {
      _id: id,
      type: 'price',
      version: '1.0.0',
      createdAt: timeStamp,
      updatedAt: timeStamp,
      unitPrices,
      createdBy: 'sku-price-handler-lambda',
      updatedBy: 'sku-price-handler-lambda',
      priceReferenceDate: referenceDate.toJSON(),
      source: priceSource
    }
    return this.mainApi.state.shipmentsDb.put(snapshot)
  }

  /**
    * @param {any} shipment
    * @param {Object.<string, {
    *   quantity: number
    *   paymentType: string
    *   checked: boolean
    *   skuMarket: string
    *   destinationSku: string
    *   originSku: string
    * }>} countsWithTranslations
    * @param {PriceSource} priceSource
    * @param {Date} referenceDate
  */
  async getUnitPrices (shipment, countsWithTranslations, priceSource, referenceDate) {
    const destinationSkus = Object.values(countsWithTranslations).map(s => `product:${s.destinationSku}`)
    let productPrices

    if (priceSource === 'CouchProduct') {
      productPrices = await this.getCouchProductPriceList(destinationSkus, referenceDate)
    } else {
      throw new Error('Unrecognized price source')
    }

    return Object.keys(shipment.counts).reduce((acc, sku) => {
      const referenceProduct = `product:${countsWithTranslations[sku] && countsWithTranslations[sku].destinationSku}`
      acc[sku] = {
        price: productPrices[referenceProduct]
      }
      return acc
    }, {})
  }

  /**
   * @param {string[]} skus
   * @param {Date} referenceDate
   */
  async getCouchProductPriceList (skus, referenceDate) {
    const formattedDate = new Date(referenceDate)
    const products = await this.mainApi.product.getByIds(skus)
    return products.reduce((acc, product) => {
      const productPrices = get(product, 'prices', [])
      const price = Number.parseFloat(this.mainApi.product.tools.getPrice(productPrices, formattedDate.toJSON()))
      acc[product._id] = Number.isNaN(price) ? null : price
      return acc
    }, {})
  }

  async decorateShipmentSnapshotsWithResolvedPrices (shipments = []) {
    if (!Array.isArray(shipments) || !shipments.length) {
      return shipments
    }

    const { client, shouldRelease } = await this.getPgClient()

    try {
      const shipmentPrices = await this.resolveShipmentPrices(shipments.map(shipment => {
        const originId = this.getLocationId(shipment.origin) || shipment.originId
        const destinationId = this.getLocationId(shipment.destination) || shipment.destinationId

        return {
          originId,
          destinationId,
          planningType: shipment.planningType,
          referenceDate: shipment.orderCreatedDate || get(shipment, 'snapshotDates.new') || get(shipment, 'snapshotDates.packed') || get(shipment, 'snapshotDates.sent') || new Date().toJSON(),
          productIds: [...new Set(Object.keys(shipment.counts || {}).map(batchId => this.getProductIdFromBatchId(batchId)).filter(Boolean))],
          serviceId: shipment.serviceId
        }
      }), client)

      return shipments.map((shipment, index) => {
        const pricesByProductId = get(shipmentPrices, `[${index}].pricesByProductId`, {})
        const translatedProductIdsByOriginalId = get(shipmentPrices, `[${index}].translatedProductIdsByOriginalId`, {})
        const counts = Object.keys(shipment.counts || {}).reduce((acc, batchId) => {
          const productId = this.getProductIdFromBatchId(batchId)
          const productPrice = pricesByProductId[productId] || {}
          acc[batchId] = {
            ...shipment.counts[batchId],
            unitSellPrice: get(productPrice, 'sellprice', null),
            unitSellPriceReferenceSku: get(translatedProductIdsByOriginalId, productId, productId),
            vat: get(productPrice, 'vat', null),
            unitSellPriceReferenceId: get(productPrice, 'sellpriceId', null),
            priceSource: get(productPrice, 'source', null)
          }
          return acc
        }, {})

        return {
          ...shipment,
          counts
        }
      })
    } finally {
      if (shouldRelease) {
        client.release()
      }
    }
  }

  async getTranslatedProductIdsForService (productIds = [], serviceId, pgClient) {
    if (!serviceId || !productIds.length) {
      return {}
    }

    const aliasesResponse = await this.mainApi.product.pgAdapter.getAliases(
      productIds.map(productId => productId.replace('product:', '')),
      serviceId,
      pgClient
    )
    const aliasesByProductId = get(aliasesResponse, 'product_aliases', {})

    return productIds.reduce((acc, productId) => {
      const productAliases = aliasesByProductId[productId.replace('product:', '')] || []
      const translatedAlias = productAliases.find(alias => alias.serviceId === serviceId)
      if (translatedAlias && translatedAlias.productId) {
        acc[productId] = `product:${translatedAlias.productId}`
      }
      return acc
    }, {})
  }

  async getResolvedPricesForShipment ({ locationId, serviceId, referenceDate, productIds = [], pgClient, planningType }) {
    if (!productIds.length) {
      return {}
    }

    const [locationPricesResponse, servicePricesResponse, generalPricesResponse] = await Promise.all([
      this.mainApi.price.adapter.getPriceList({
        skus: productIds,
        locationFsid: locationId,
        referenceDate
      }, pgClient),
      serviceId
        ? this.mainApi.price.adapter.getPriceList({
          skus: productIds,
          serviceId,
          referenceDate
        }, pgClient)
        : Promise.resolve({ prices: [] }),
      this.mainApi.price.adapter.getPriceList({
        skus: productIds,
        referenceDate
      }, pgClient)
    ])

    const locationPricesBySku = keyBy(locationPricesResponse.prices || [], 'sku')
    const servicePricesBySku = keyBy(servicePricesResponse.prices || [], 'sku')
    const generalPricesBySku = keyBy(generalPricesResponse.prices || [], 'sku')

    return productIds.reduce((acc, productId) => {
      const locationPrice = locationPricesBySku[productId]
      const servicePrice = servicePricesBySku[productId]
      const generalPrice = generalPricesBySku[productId]

      const isResupply = planningType === 'resupply'
      let selectedPrice = locationPrice
      let source = 'location'

      if (!this.hasPrice(locationPrice, isResupply)) {
        selectedPrice = servicePrice
        source = 'service'
      }

      if (!this.hasPrice(selectedPrice, isResupply)) {
        selectedPrice = generalPrice
        source = 'general'
      }

      const fallbackPrice = selectedPrice || locationPrice || servicePrice || generalPrice || {}
      const priceKey = isResupply ? 'buyprice' : 'sellprice'
      const priceIdKey = isResupply ? 'buypriceId' : 'sellpriceId'

      acc[productId] = {
        ...fallbackPrice,
        sku: productId,
        source,
        sellprice: selectedPrice && selectedPrice[priceKey] != null ? selectedPrice[priceKey] : null,
        sellpriceId: selectedPrice && selectedPrice[priceIdKey] ? selectedPrice[priceIdKey] : null,
        vat: fallbackPrice && Object.prototype.hasOwnProperty.call(fallbackPrice, 'vat') ? fallbackPrice.vat : null
      }
      return acc
    }, {})
  }

  hasPrice (price, isResupply = false) {
    const priceKey = isResupply ? 'buyprice' : 'sellprice'
    return Boolean(price) && price[priceKey] !== null && typeof price[priceKey] !== 'undefined'
  }

  async getPgClient () {
    if (!this.pgConnection) {
      return { client: undefined, shouldRelease: false }
    }

    const shouldRelease = !('connection' in this.pgConnection)
    const client = shouldRelease ? await this.pgConnection.connect() : this.pgConnection
    return { client, shouldRelease }
  }

  getLocationId (location) {
    if (!location) {
      return null
    }

    if (typeof location === 'string') {
      return location
    }

    return location.id || null
  }

  getProductIdFromBatchId (batchId = '') {
    return batchId.split(':manufacturer')[0] || null
  }
}

module.exports = ShipmentPriceListApi
